import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { TaxRateSet } from '@/api/models';
import { useTaxSettingsHistoryQuery } from '@/api/queries';
import { unwrapStatement } from '@/pages/accounting/statements/statementMeta';
import { todayIso } from '../../settingsMeta';

/** One history row, with the standing it has on today's timeline. */
export type RateHistoryRow = {
    set: TaxRateSet;
    /** The set governing today. Exactly one, when any set is in force. */
    isInForce: boolean;
    /** Dated in the future — recorded, but not yet applying. */
    isScheduled: boolean;
};

export type UseRateHistoryResult = {
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    rows: RateHistoryRow[];
    isEmpty: boolean;
};

/**
 * The audit view: every recorded change, newest first.
 *
 * The rows are annotated rather than merely listed. A history that shows six
 * changes without saying which one is *in force* leaves the reader to work it
 * out from dates, which is exactly the question they came to answer.
 */
export const useRateHistory = (): UseRateHistoryResult => {
    const query = useTaxSettingsHistoryQuery();
    const { data, error } = unwrapStatement(query.data, query.error);

    const rows = React.useMemo<RateHistoryRow[]>(() => {
        const sets = data?.sets ?? [];
        const today = todayIso();
        // The server returns newest first, so the first set already in effect
        // is the one governing today.
        const inForceId = sets.find((set) => set.effectiveFrom <= today)?.id;
        return sets.map((set) => ({
            set,
            isInForce: set.id === inForceId,
            isScheduled: set.effectiveFrom > today,
        }));
    }, [data]);

    const onRetry = React.useCallback(() => {
        query.refetch();
    }, [query]);

    return {
        isLoading: query.isLoading,
        error,
        onRetry,
        rows,
        // Only "none" once the feed has resolved — an empty list while a query
        // is in flight means "not known yet".
        isEmpty: !query.isLoading && rows.length === 0,
    };
};
